import { Component, computed, effect, inject, input } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { FormFieldService } from '../../../services/form-field/form-field.service';
import {
  FormFieldLabels,
  SelectFormFieldOptions,
} from '../../../form-field.types';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { ErrorStateMatcher } from '@angular/material/core';

@Component({
  selector: 'app-select-form-field',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatIconModule,
    MatSelectModule,
  ],
  templateUrl: './select-form-field.component.html',
  styleUrl: './select-form-field.component.scss',
})
export class SelectFormFieldComponent {
  private formFieldService = inject(FormFieldService);
  private lang = inject(LangService).lang;

  name = input('');
  path = input<string>();
  labels = input<FormFieldLabels>();
  options = input<SelectFormFieldOptions>();
  errorStateMatcher = input<ErrorStateMatcher>({
    isErrorState: control => !!(control?.touched && control?.errors),
  });

  private formGroup = this.formFieldService.formGroup;
  control = computed(() => {
    const path = this.path() ?? this.name();
    const formGroup = this.formGroup();
    return formGroup?.get(path) as FormControl;
  });

  label = computed(() => this.labels()?.label);
  placeholder = computed(() => this.labels()?.placeholder ?? '');
  help = computed(() => this.labels()?.help);
  error = this.formFieldService.controlErrorMessage(this.name, this.path);
  selectOptions = computed(() => this.labels()?.options ?? []);
  required = computed(() => !!this.options()?.required);
  useEmpty = computed(() => !!this.options()?.useEmpty);
  emptyLabel = computed(() => {
    const emptyLabel = this.options()?.emptyLabel;
    if (typeof emptyLabel === 'function') {
      return emptyLabel(this.lang);
    }
    if (typeof emptyLabel === 'string') {
      return emptyLabel;
    }
    const none = this.lang.shared.standardInputs.select.none;
    return `-- ${none} --`;
  });
  private configureControl = computed(() => this.options()?.configureControl);

  private configureControlEffect = () => {
    const configureControl = this.configureControl();
    const control = this.control();
    if (control && configureControl) {
      configureControl(control);
    }
  };

  constructor() {
    effect(this.configureControlEffect);
  }
}
